import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, addDays, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import {
  LayoutDashboard, Calendar, LogOut, MapPin, Clock, User,
  Package, Truck, ChevronLeft, ChevronRight, Phone, FileText, CheckCircle, Mail, Map, X, Crown, Repeat, Building, Navigation, MessageSquare, Play, Square
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Custom markers for pickups (pink) and deliveries (green)
const pickupIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="background-color: #E91E8C; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path></svg>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

const deliveryIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="background-color: #10B981; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path><path d="M15 18H9"></path><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path><circle cx="17" cy="18" r="2"></circle><circle cx="7" cy="18" r="2"></circle></svg>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPlanning = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [pickups, setPickups] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [geocodingInProgress, setGeocodingInProgress] = useState(false);
  
  // Live tracking states
  const [trackingActive, setTrackingActive] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [notifyingClient, setNotifyingClient] = useState(null);

  const getAuth = useCallback(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  }, [navigate]);

  const fetchPlanning = useCallback(async () => {
    const headers = getAuth();
    if (!headers) return;

    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await axios.get(`${API}/admin/planning/${dateStr}`, { headers });
      setPickups(response.data.pickups || []);
      setDeliveries(response.data.deliveries || []);
    } catch (error) {
      console.error("Error fetching planning:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("adminAuth");
        navigate("/admin");
      }
      toast.error("Erreur lors du chargement du planning");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, getAuth, navigate]);

  useEffect(() => {
    fetchPlanning();
  }, [fetchPlanning]);

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  const markTaskCompleted = async (bookingId, taskType) => {
    const headers = getAuth();
    if (!headers) return;

    try {
      await axios.post(
        `${API}/admin/bookings/${bookingId}/complete-task?task_type=${taskType}`,
        {},
        { headers }
      );
      toast.success(`${taskType === "pickup" ? "Ramassage" : "Livraison"} marqué comme fait !`);
      fetchPlanning(); // Refresh the list
    } catch (error) {
      console.error("Error marking task completed:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const goToPreviousDay = () => setSelectedDate(prev => subDays(prev, 1));
  const goToNextDay = () => setSelectedDate(prev => addDays(prev, 1));
  const goToToday = () => setSelectedDate(new Date());

  // Geocode address using Nominatim (OpenStreetMap) - with France context
  const geocodeAddress = async (address) => {
    try {
      // Add France to improve geocoding accuracy
      const addressWithCountry = address.includes("France") ? address : `${address}, France`;
      
      // First attempt with full address
      let response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressWithCountry)}&limit=1&countrycodes=fr`,
        { headers: { "User-Agent": "PressingSakura/1.0" } }
      );
      let data = await response.json();
      
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
      
      // Second attempt: extract city/postal code for a broader search
      const postalMatch = address.match(/\b(69\d{3}|26\d{3}|38\d{3}|01\d{3}|42\d{3})\b/);
      if (postalMatch) {
        const postalCode = postalMatch[1];
        response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(postalCode + " France")}&limit=1&countrycodes=fr`,
          { headers: { "User-Agent": "PressingSakura/1.0" } }
        );
        data = await response.json();
        if (data && data.length > 0) {
          return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        }
      }
      
      return null;
    } catch (error) {
      console.error("Geocoding error:", error);
      return null;
    }
  };

  // Open map with all tasks
  const openMapView = async () => {
    if (pickups.length === 0 && deliveries.length === 0) {
      toast.error("Aucune tâche à afficher sur la carte");
      return;
    }

    setGeocodingInProgress(true);
    setShowMap(true);
    
    const markers = [];
    const failedAddresses = [];
    
    // Geocode pickups
    for (const pickup of pickups) {
      const coords = await geocodeAddress(pickup.address);
      if (coords) {
        markers.push({
          ...pickup,
          type: "pickup",
          lat: coords.lat,
          lng: coords.lng,
        });
      } else {
        failedAddresses.push({ name: pickup.name, address: pickup.address, type: "Ramassage" });
      }
      // Small delay to respect Nominatim rate limiting
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Geocode deliveries
    for (const delivery of deliveries) {
      const coords = await geocodeAddress(delivery.address);
      if (coords) {
        markers.push({
          ...delivery,
          type: "delivery",
          lat: coords.lat,
          lng: coords.lng,
        });
      } else {
        failedAddresses.push({ name: delivery.name, address: delivery.address, type: "Livraison" });
      }
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    setMapMarkers(markers);
    setGeocodingInProgress(false);
    
    if (markers.length === 0) {
      toast.error("Impossible de localiser les adresses");
    } else if (failedAddresses.length > 0) {
      toast.warning(`${failedAddresses.length} adresse(s) non trouvée(s): ${failedAddresses.map(f => f.name).join(", ")}`);
    }
  };

  // ===== LIVE TRACKING FUNCTIONS =====
  
  // Start live tracking - share position
  const startTracking = async () => {
    if (!("geolocation" in navigator)) {
      toast.error("La géolocalisation n'est pas disponible sur cet appareil");
      return;
    }
    
    const headers = getAuth();
    if (!headers) return;
    
    try {
      // Notify server that tracking is starting
      await axios.post(`${API}/admin/tracking/start`, {}, { headers });
      
      // Start watching position
      const id = navigator.geolocation.watchPosition(
        async (position) => {
          try {
            await axios.post(`${API}/admin/tracking/position`, {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            }, { headers });
          } catch (err) {
            console.error("Error updating position:", err);
          }
        },
        (error) => {
          console.error("Geolocation error:", error);
          toast.error("Erreur de géolocalisation: " + error.message);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 10000,
          timeout: 15000
        }
      );
      
      setWatchId(id);
      setTrackingActive(true);
      toast.success("Tracking démarré ! Votre position est partagée.");
    } catch (error) {
      toast.error("Erreur lors du démarrage du tracking");
    }
  };
  
  // Stop live tracking
  const stopTracking = async () => {
    const headers = getAuth();
    if (!headers) return;
    
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    
    try {
      await axios.post(`${API}/admin/tracking/stop`, {}, { headers });
      setTrackingActive(false);
      toast.success("Tracking arrêté");
    } catch (error) {
      console.error("Error stopping tracking:", error);
    }
  };
  
  // Notify client via SMS
  const notifyClient = async (bookingId, phone, clientName, taskType = "delivery") => {
    if (!phone) {
      toast.error("Ce client n'a pas de numéro de téléphone");
      return;
    }
    
    const headers = getAuth();
    if (!headers) return;
    
    setNotifyingClient(bookingId);
    
    try {
      await axios.post(`${API}/admin/notify-client`, {
        booking_id: bookingId,
        phone: phone,
        task_type: taskType
      }, { headers });
      
      toast.success(`SMS envoyé à ${clientName} !`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du SMS");
    } finally {
      setNotifyingClient(null);
    }
  };

  // Calculate map center
  const getMapCenter = () => {
    if (mapMarkers.length === 0) {
      // Default to Lyon center
      return [45.75, 4.85];
    }
    const avgLat = mapMarkers.reduce((sum, m) => sum + m.lat, 0) / mapMarkers.length;
    const avgLng = mapMarkers.reduce((sum, m) => sum + m.lng, 0) / mapMarkers.length;
    return [avgLat, avgLng];
  };

  const getZoneBadge = (zoneId) => {
    if (zoneId === "lyon") {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Lyon</Badge>;
    } else if (zoneId === "francheville") {
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Francheville</Badge>;
    }
    return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">St-Rambert</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { class: "bg-amber-100 text-amber-700", label: "En attente" },
      confirmed: { class: "bg-blue-100 text-blue-700", label: "Confirmé" },
      delivered: { class: "bg-green-100 text-green-700", label: "Livré" },
      cancelled: { class: "bg-red-100 text-red-700", label: "Annulé" },
    };
    const variant = variants[status] || variants.pending;
    return <Badge className={variant.class}>{variant.label}</Badge>;
  };

  // Combined card for pickup + delivery on same day
  const CombinedTaskCard = ({ pickup, delivery }) => {
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pickup.address)}`;
    const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(pickup.address)}&navigate=yes`;
    const isPickupCompleted = pickup.completed;
    const isDeliveryCompleted = delivery.completed;
    const allCompleted = isPickupCompleted && isDeliveryCompleted;
    
    return (
      <div 
        className={`p-4 rounded-xl border-l-4 bg-white shadow-sm ${
          allCompleted 
            ? "border-l-gray-300 opacity-60" 
            : "border-l-purple-500"
        }`}
        data-testid={`task-combined-${pickup.booking_id}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Package className={`w-4 h-4 ${isPickupCompleted ? "text-gray-400" : "text-pink-500"}`} />
                <span className="text-purple-600">+</span>
                <Truck className={`w-4 h-4 ${isDeliveryCompleted ? "text-gray-400" : "text-green-500"}`} />
              </div>
              <button
                type="button"
                onClick={() => navigate(`/admin/customers?search=${encodeURIComponent(pickup.email || pickup.phone || `${pickup.first_name} ${pickup.last_name}`)}`)}
                className={`font-semibold cursor-pointer ${allCompleted ? "text-gray-500 line-through" : "text-pink-600 hover:text-pink-800 hover:underline"} transition-colors`}
                title="Voir la fiche client"
              >
                {pickup.first_name} {pickup.last_name}
              </button>
              {pickup.is_pro && (
                <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-1.5 py-0.5 flex items-center gap-1">
                  <Building className="w-3 h-3" />
                  PRO
                </Badge>
              )}
              {pickup.is_vip && (
                <Badge className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-1.5 py-0.5 flex items-center gap-1">
                  <Crown className="w-3 h-3" />
                  VIP
                </Badge>
              )}
              {pickup.is_recurring && (
                <Badge className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 flex items-center gap-1">
                  <Repeat className="w-3 h-3" />
                  Récurrent
                </Badge>
              )}
              {getZoneBadge(pickup.zone_id)}
              {getStatusBadge(pickup.status)}
            </div>
            
            <div className="space-y-2 text-sm">
              {/* Pickup time */}
              <div className="flex items-center gap-2 text-slate-600">
                <Package className={`w-4 h-4 ${isPickupCompleted ? "text-gray-400" : "text-pink-400"}`} />
                <span className={`font-medium ${isPickupCompleted ? "line-through text-gray-400" : ""}`}>
                  Ramassage: {pickup.start_time} - {pickup.end_time}
                </span>
                {isPickupCompleted && <Badge className="bg-green-100 text-green-700 text-xs">Fait</Badge>}
              </div>
              
              {/* Delivery time */}
              <div className="flex items-center gap-2 text-slate-600">
                <Truck className={`w-4 h-4 ${isDeliveryCompleted ? "text-gray-400" : "text-green-400"}`} />
                <span className={`font-medium ${isDeliveryCompleted ? "line-through text-gray-400" : ""}`}>
                  Livraison: {delivery.start_time} - {delivery.end_time}
                </span>
                {isDeliveryCompleted && <Badge className="bg-green-100 text-green-700 text-xs">Fait</Badge>}
              </div>
              
              {/* Address */}
              <div className="flex items-start gap-2 text-slate-600">
                <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="flex flex-col gap-1">
                  <span>{pickup.address}</span>
                  <div className="flex gap-2 mt-1">
                    <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" 
                       className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                      <Map className="w-3 h-3" /> Google Maps
                    </a>
                    <a href={wazeUrl} target="_blank" rel="noopener noreferrer"
                       className="text-purple-600 hover:underline text-xs">
                      Waze
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Contact */}
              {pickup.phone && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <a href={`tel:${pickup.phone}`} className="text-blue-600 hover:underline">{pickup.phone}</a>
                </div>
              )}
              {pickup.email && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${pickup.email}`} className="text-blue-600 hover:underline text-xs">{pickup.email}</a>
                </div>
              )}
              
              {/* Remarks */}
              {pickup.remarks && (
                <div className="flex items-start gap-2 bg-amber-50 p-2 rounded-lg mt-2">
                  <FileText className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-amber-700 text-sm">{pickup.remarks}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Mark as complete buttons */}
          <div className="flex flex-col gap-2">
            {!isPickupCompleted && (
              <Button
                variant="outline"
                size="sm"
                className="text-pink-600 border-pink-300 hover:bg-pink-50"
                onClick={() => markTaskCompleted(pickup.booking_id, "pickup")}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Ramassage fait
              </Button>
            )}
            {!isDeliveryCompleted && (
              <Button
                variant="outline"
                size="sm"
                className="text-green-600 border-green-300 hover:bg-green-50"
                onClick={() => markTaskCompleted(delivery.booking_id, "delivery")}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Livraison faite
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const TaskCard = ({ task, type }) => {
    // Create navigation URLs
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(task.address)}`;
    const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(task.address)}&navigate=yes`;
    const isCompleted = task.completed;
    
    return (
    <div 
      className={`p-4 rounded-xl border-l-4 bg-white shadow-sm ${
        isCompleted 
          ? "border-l-gray-300 opacity-60" 
          : type === "pickup" ? "border-l-pink-500" : "border-l-green-500"
      }`}
      data-testid={`task-${task.booking_id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {type === "pickup" ? (
              <Package className={`w-4 h-4 ${isCompleted ? "text-gray-400" : "text-pink-500"}`} />
            ) : (
              <Truck className={`w-4 h-4 ${isCompleted ? "text-gray-400" : "text-green-500"}`} />
            )}
            <button
              type="button"
              onClick={() => navigate(`/admin/customers?search=${encodeURIComponent(task.email || task.phone || `${task.first_name} ${task.last_name}`)}`)}
              className={`font-semibold cursor-pointer ${isCompleted ? "text-gray-500 line-through" : "text-pink-600 hover:text-pink-800 hover:underline"} transition-colors`}
              title="Voir la fiche client"
              data-testid={`client-link-${task.booking_id}`}
            >
              {task.first_name} {task.last_name}
            </button>
            {task.is_pro && (
              <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-1.5 py-0.5 flex items-center gap-1" title="Client professionnel">
                <Building className="w-3 h-3" />
                PRO
              </Badge>
            )}
            {task.is_vip && (
              <Badge className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-1.5 py-0.5 flex items-center gap-1" title="Réservation prioritaire">
                <Crown className="w-3 h-3" />
                VIP
              </Badge>
            )}
            {task.is_recurring && (
              <Badge className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 flex items-center gap-1" title="Ramassage & Livraison récurrent">
                <Repeat className="w-3 h-3" />
                Récurrent
              </Badge>
            )}
            {getZoneBadge(task.zone_id)}
            {getStatusBadge(task.status)}
            {isCompleted && (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" /> Fait
              </Badge>
            )}
          </div>
          
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="font-medium">{task.start_time} - {task.end_time}</span>
            </div>
            
            <div className="flex items-start gap-2 text-slate-600">
              <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="flex flex-col gap-1">
                <span>{task.address}</span>
                <div className="flex gap-2 mt-1">
                  <a 
                    href={googleMapsUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                    title="Ouvrir dans Google Maps"
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    Maps
                  </a>
                  <a 
                    href={wazeUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-cyan-100 text-cyan-700 rounded-full hover:bg-cyan-200 transition-colors"
                    title="Ouvrir dans Waze"
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.54 6.63c.69 1.16 1.04 2.46 1.04 3.83 0 1.41-.38 2.76-1.07 3.94-.7 1.21-1.71 2.22-2.91 2.94-.06.04-.12.08-.18.11-.02.01-.04.02-.06.04-.25.14-.51.27-.78.39-.03.01-.06.02-.08.03-.14.06-.28.11-.42.16-.05.02-.1.04-.16.05-.14.05-.28.09-.43.13-.05.01-.09.03-.14.04-.19.05-.38.09-.58.13-.04.01-.07.01-.11.02-.17.03-.33.05-.5.07-.05.01-.11.01-.16.02-.2.02-.4.03-.6.03h-.28c-.22 0-.43-.01-.65-.03-.05 0-.1-.01-.15-.01-.18-.02-.36-.04-.54-.07-.04-.01-.08-.01-.12-.02-.21-.04-.42-.08-.62-.14-.03-.01-.06-.01-.09-.02-.16-.04-.31-.09-.47-.14-.05-.02-.1-.03-.14-.05-.14-.05-.28-.1-.42-.16-.03-.01-.07-.03-.1-.04-.27-.12-.53-.25-.79-.4-.02-.01-.03-.02-.05-.03-.07-.04-.13-.08-.2-.12-1.21-.73-2.21-1.74-2.91-2.95-.69-1.19-1.07-2.54-1.07-3.95 0-1.37.35-2.67 1.04-3.83.68-1.18 1.67-2.17 2.85-2.87l.18-.11c.03-.01.05-.03.08-.04.24-.13.49-.25.75-.36l.12-.05c.13-.05.26-.1.4-.14.06-.02.12-.04.18-.06.12-.04.25-.07.37-.1.07-.02.13-.04.2-.05.17-.04.34-.07.51-.1.05-.01.11-.02.16-.02.2-.03.4-.05.6-.06.04 0 .08-.01.13-.01.26-.01.52-.02.78 0h.13c.21.01.42.03.62.05.05.01.1.01.15.02.18.03.36.06.54.1.06.01.11.03.17.04.13.03.26.07.39.11.07.02.15.05.22.07.12.04.24.08.35.13.08.03.16.06.24.1.11.05.22.09.33.15.08.04.17.08.25.12.1.05.21.11.31.16.09.05.17.1.26.15.1.06.2.12.29.18.09.06.17.11.25.17.1.07.19.14.28.21.08.06.16.13.23.19.1.08.19.16.28.25.07.06.14.13.21.2.1.1.2.2.29.3.06.06.11.12.16.18.12.14.24.28.35.43.03.04.07.09.1.13.22.3.42.61.6.94zm-5.06 9.89l.02-.02c.11-.07.22-.14.32-.22.01-.01.03-.02.04-.03.1-.08.2-.15.29-.24l.02-.02c.1-.09.19-.18.28-.27l.01-.01c.09-.1.18-.2.26-.3l.01-.01c.08-.1.16-.21.24-.32l.01-.02c.07-.11.15-.22.21-.33l.01-.02c.07-.12.13-.24.19-.36l.01-.03c.06-.12.11-.25.16-.38 0-.01.01-.02.01-.03.05-.13.09-.26.13-.39v-.02c.04-.14.07-.28.1-.42v-.01c.02-.14.04-.29.05-.43v-.04c.01-.14.02-.28.02-.42v-.08c0-.14-.01-.28-.02-.42v-.06c-.01-.14-.03-.29-.06-.43v-.01c-.03-.14-.06-.28-.1-.42v-.02c-.04-.13-.08-.27-.13-.4v-.02c-.05-.13-.1-.26-.16-.38-.01-.02-.01-.03-.02-.05-.06-.12-.12-.24-.19-.35-.01-.02-.02-.04-.03-.05-.07-.11-.14-.22-.22-.33-.01-.01-.02-.03-.03-.04-.08-.1-.16-.21-.25-.31-.01-.01-.02-.03-.03-.04-.09-.1-.18-.2-.28-.29l-.02-.02c-.1-.09-.2-.19-.3-.27-.01-.01-.02-.02-.04-.03-.1-.08-.21-.16-.32-.24-.01-.01-.03-.02-.04-.03-.11-.07-.23-.14-.35-.21-.01-.01-.03-.01-.04-.02-.12-.07-.25-.13-.37-.19-.02-.01-.03-.01-.05-.02-.13-.05-.25-.11-.38-.15-.02-.01-.04-.01-.06-.02-.13-.04-.26-.08-.4-.11-.02 0-.04-.01-.06-.01-.14-.03-.28-.06-.43-.08h-.05c-.15-.02-.29-.03-.44-.04h-.05c-.15 0-.3 0-.45.01h-.03c-.15.01-.3.03-.45.05-.02 0-.03.01-.05.01-.14.03-.28.06-.42.1-.02.01-.05.01-.07.02-.13.04-.27.09-.4.14-.02.01-.05.02-.07.03-.13.06-.25.12-.37.19-.02.01-.04.02-.06.04-.12.07-.23.14-.34.22-.02.01-.04.03-.06.04-.1.08-.21.16-.31.25-.02.02-.04.03-.05.05-.1.09-.2.19-.29.29-.01.01-.02.02-.03.04-.09.1-.18.21-.26.32-.01.02-.02.03-.04.05-.08.11-.15.22-.22.34l-.03.06c-.06.11-.13.23-.18.35-.01.02-.02.05-.03.07-.06.12-.11.25-.15.37-.01.03-.02.05-.02.08-.04.13-.08.26-.11.39-.01.03-.01.05-.02.08-.03.14-.05.28-.07.42 0 .03-.01.05-.01.08-.01.14-.02.29-.03.44v.08c0 .15.01.29.02.44 0 .03.01.06.01.09.02.14.04.28.07.42.01.03.01.06.02.09.03.13.07.27.12.4 0 .02.01.05.02.07.05.13.1.26.16.38.01.02.02.04.03.06.06.12.13.24.2.35.01.02.02.04.04.06.07.11.15.22.24.33.01.02.03.03.04.05.09.1.18.21.28.3.01.01.02.02.03.03.1.1.2.19.31.28.01.01.03.02.04.03.11.08.22.16.33.24.02.01.04.02.05.03.12.07.24.14.36.2.02.01.03.02.05.02.13.06.26.12.39.17.02.01.04.01.05.02.14.04.27.09.41.12.02 0 .04.01.06.01.14.03.29.05.44.07h.05c.15.02.3.02.46.02h.03c.15 0 .31-.01.46-.02h.03c.15-.02.29-.04.44-.06.02 0 .04-.01.06-.01.14-.03.27-.07.4-.11.02-.01.05-.01.07-.02.13-.05.25-.1.38-.16.02-.01.04-.02.07-.03.12-.06.24-.13.35-.21z"/>
                    </svg>
                    Waze
                  </a>
                </div>
              </div>
            </div>
            
            {task.phone && (
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-4 h-4 text-slate-400" />
                <a href={`tel:${task.phone}`} className="hover:text-primary">{task.phone}</a>
              </div>
            )}

            {task.email && (
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4 text-slate-400" />
                <a href={`mailto:${task.email}`} className="hover:text-primary">{task.email}</a>
              </div>
            )}
            
            {task.remarks && (
              <div className="flex items-start gap-2 text-slate-500 italic">
                <FileText className="w-4 h-4 text-slate-400 mt-0.5" />
                <span>{task.remarks}</span>
              </div>
            )}
            
            {/* Show delivery date for pickups */}
            {type === "pickup" && task.delivery_date && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
                <Truck className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">
                  Livraison prévue : {format(new Date(task.delivery_date), "EEEE d MMMM", { locale: fr })}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Mark as Done button */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          {/* Notify button for both pickups and deliveries - shows when tracking active OR always with phone */}
          {!isCompleted && task.phone && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => notifyClient(task.booking_id, task.phone, task.first_name, type)}
              disabled={notifyingClient === task.booking_id || !trackingActive}
              className={`text-blue-600 border-blue-300 hover:bg-blue-50 ${!trackingActive ? 'opacity-50' : ''}`}
              data-testid={`notify-${task.booking_id}`}
              title={!trackingActive ? "Activez le suivi en temps réel pour notifier" : "Envoyer SMS au client"}
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              {notifyingClient === task.booking_id ? "..." : "Notifier"}
            </Button>
          )}
          
          {!isCompleted && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markTaskCompleted(task.booking_id, type)}
              className="text-green-600 border-green-300 hover:bg-green-50"
              data-testid={`complete-${type}-${task.booking_id}`}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Fait
            </Button>
          )}
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="admin-sidebar hidden md:block">
        <div className="flex items-center justify-center mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
            alt="Pressing Sakura" 
            className="h-16 w-auto bg-white rounded-lg p-1"
          />
        </div>

        <nav className="space-y-2">
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/dashboard")}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Tableau de bord</span>
          </div>
          <div className="admin-nav-item active" data-testid="nav-planning">
            <Calendar className="w-5 h-5" />
            <span>Planning</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/slots")}
            data-testid="nav-slots"
          >
            <Clock className="w-5 h-5" />
            <span>Gérer les créneaux</span>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6">
          <button
            onClick={handleLogout}
            className="admin-nav-item text-slate-400 hover:text-white"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 md:p-10 overflow-auto pb-20 md:pb-10">
        {/* Mobile Header with Navigation */}
        <div className="md:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Planning
            </h1>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
          {/* Mobile Navigation Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/dashboard")} className="flex-shrink-0">
              <LayoutDashboard className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <Button size="sm" className="bg-primary text-white flex-shrink-0">
              <Calendar className="w-4 h-4 mr-1" />
              Planning
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/slots")} className="flex-shrink-0">
              <Clock className="w-4 h-4 mr-1" />
              Créneaux
            </Button>
          </div>
        </div>

        {/* Date Navigation */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Button variant="outline" size="icon" onClick={goToPreviousDay}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <div className="text-center">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}
                </h2>
                <Button 
                  variant="link" 
                  className="text-primary"
                  onClick={goToToday}
                >
                  Aujourd'hui
                </Button>
              </div>
              
              <Button variant="outline" size="icon" onClick={goToNextDay}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Live Tracking Controls */}
        <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-slate-700">Suivi en temps réel</span>
                {trackingActive && (
                  <Badge className="bg-green-500 text-white animate-pulse">
                    Actif
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                {!trackingActive ? (
                  <Button 
                    onClick={startTracking}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Démarrer la tournée
                  </Button>
                ) : (
                  <Button 
                    onClick={stopTracking}
                    variant="destructive"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Arrêter
                  </Button>
                )}
              </div>
            </div>
            {trackingActive && (
              <p className="text-sm text-blue-600 mt-2">
                📍 Votre position est partagée. Cliquez sur "Notifier" à côté d'un client pour lui envoyer un SMS.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ramassages</p>
                  <p className="text-2xl font-bold">{pickups.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Truck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Livraisons</p>
                  <p className="text-2xl font-bold">{deliveries.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map Button */}
        {(pickups.length > 0 || deliveries.length > 0) && (
          <div className="mb-6">
            <Button 
              onClick={openMapView}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="view-map-btn"
            >
              <Map className="w-5 h-5 mr-2" />
              Voir sur la carte ({pickups.length + deliveries.length} adresses)
            </Button>
          </div>
        )}

        {/* Map Modal */}
        {showMap && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-4">
                  <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Carte des tâches du jour
                  </h3>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-pink-500"></span>
                      Ramassages ({pickups.length})
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      Livraisons ({deliveries.length})
                    </span>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowMap(false)}
                  data-testid="close-map-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="h-[500px] relative">
                {geocodingInProgress ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50">
                    <div className="animate-spin w-10 h-10 border-3 border-primary border-t-transparent rounded-full mb-4"></div>
                    <p className="text-slate-600">Localisation des adresses en cours...</p>
                  </div>
                ) : mapMarkers.length > 0 ? (
                  <MapContainer 
                    center={getMapCenter()} 
                    zoom={12} 
                    style={{ height: "100%", width: "100%" }}
                    key={mapMarkers.map(m => m.booking_id).join("-")}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {mapMarkers.map((marker, index) => (
                      <Marker 
                        key={`${marker.type}-${marker.booking_id}-${index}`}
                        position={[marker.lat, marker.lng]}
                        icon={marker.type === "pickup" ? pickupIcon : deliveryIcon}
                      >
                        <Popup>
                          <div className="min-w-[200px]">
                            <div className="flex items-center gap-2 mb-2">
                              {marker.type === "pickup" ? (
                                <Package className="w-4 h-4 text-pink-500" />
                              ) : (
                                <Truck className="w-4 h-4 text-green-500" />
                              )}
                              <strong>{marker.type === "pickup" ? "Ramassage" : "Livraison"}</strong>
                            </div>
                            <p className="font-semibold">{marker.first_name} {marker.last_name}</p>
                            <p className="text-sm text-gray-600">{marker.address}</p>
                            <p className="text-sm text-gray-500 mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {marker.start_time} - {marker.end_time}
                            </p>
                            {marker.phone && (
                              <p className="text-sm mt-1">
                                <Phone className="w-3 h-3 inline mr-1" />
                                <a href={`tel:${marker.phone}`} className="text-blue-600">{marker.phone}</a>
                              </p>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
                    <p className="text-slate-600">Aucune adresse localisée</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Planning List */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Tâches du jour
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : pickups.length === 0 && deliveries.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune tâche prévue pour ce jour</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Combine and group by booking_id (same client same day) */}
                {(() => {
                  const allTasks = [
                    ...pickups.map(p => ({ ...p, type: "pickup" })),
                    ...deliveries.map(d => ({ ...d, type: "delivery" }))
                  ];
                  
                  // Group by booking_id
                  const groupedByBooking = {};
                  allTasks.forEach(task => {
                    const key = task.booking_id;
                    if (!groupedByBooking[key]) {
                      groupedByBooking[key] = { pickup: null, delivery: null, task };
                    }
                    if (task.type === 'pickup') {
                      groupedByBooking[key].pickup = task;
                    } else {
                      groupedByBooking[key].delivery = task;
                    }
                  });
                  
                  // Convert to array and sort by first task time
                  return Object.values(groupedByBooking)
                    .map(group => {
                      const firstTask = group.pickup || group.delivery;
                      return { ...group, sortTime: firstTask.start_time };
                    })
                    .sort((a, b) => a.sortTime.localeCompare(b.sortTime))
                    .map((group, index) => {
                      if (group.pickup && group.delivery) {
                        // Combined: same client has both pickup and delivery
                        return <CombinedTaskCard key={`combined-${group.pickup.booking_id}-${index}`} pickup={group.pickup} delivery={group.delivery} />;
                      } else if (group.pickup) {
                        return <TaskCard key={`pickup-${group.pickup.booking_id}-${index}`} task={group.pickup} type="pickup" />;
                      } else {
                        return <TaskCard key={`delivery-${group.delivery.booking_id}-${index}`} task={group.delivery} type="delivery" />;
                      }
                    });
                })()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mobile Navigation */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
          <div className="flex justify-around">
            <button
              className="flex flex-col items-center text-slate-500"
              onClick={() => navigate("/admin/dashboard")}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-xs mt-1">Dashboard</span>
            </button>
            <button className="flex flex-col items-center text-primary">
              <Calendar className="w-5 h-5" />
              <span className="text-xs mt-1">Planning</span>
            </button>
            <button
              className="flex flex-col items-center text-slate-500"
              onClick={() => navigate("/admin/slots")}
            >
              <Clock className="w-5 h-5" />
              <span className="text-xs mt-1">Créneaux</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPlanning;
